import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { Chip, type ChipTone } from '../components/Chip';
import { Nav } from '../components/Nav';
import { api, type InsuranceItem, type ProbabilityLadderRow } from '../lib/api';

type Priority = 'critical' | 'high' | 'medium' | 'optional';

// Loudest to quietest, top to bottom: the solid-ink chip would out-shout the
// red one, so `medium` takes the neutral grey chip with ink text instead.
const priorityTone: Record<Priority, ChipTone> = {
  critical: 'danger',
  high: 'warn',
  medium: 'neutral',
  optional: 'muted',
};

export default function Insurance() {
  const [items, setItems] = useState<InsuranceItem[]>([]);
  const [ladder, setLadder] = useState<ProbabilityLadderRow[]>([]);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.getInsurance();
      setItems(r.items);
      setLadder(r.probability_ladder);
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (id: string, next: boolean) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.patchInsurance(id, { active: next });
      await load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const groups: Array<[Priority, string]> = [
    ['critical', 'Critical'],
    ['high', 'High'],
    ['medium', 'Medium'],
    ['optional', 'Optional'],
  ];
  const byGroup = (g: Priority) => items.filter((i) => i.priority === g);

  return (
    <>
      <div className="min-h-full max-w-md lg:max-w-5xl mx-auto px-5 lg:px-8 py-10 lg:py-12 space-y-6 pb-32 stack-2col">
        <header className="space-y-3">
          <Link to="/dashboard" className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-faint hover:text-fg">
            ← Dashboard
          </Link>
          <h1 className="font-display text-5xl leading-none">Insurance stack</h1>
          <p className="text-sm text-fg-muted leading-relaxed">
            Stack interventions in priority order. Each one nudges the win probability up.
          </p>
        </header>

        {ladder.length > 0 && (
          <Card eyebrow="Probability ladder">
            <ul className="space-y-2">
              {ladder.map((r) => (
                <li key={r.label} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-fg flex-1">{r.label}</span>
                  <span className="font-mono tabular-nums text-fg font-medium">{r.pct}%</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {groups.map(([key, label]) => {
          const list = byGroup(key);
          if (list.length === 0) return null;
          return (
            <Card
              key={key}
              eyebrow={label}
              action={<Chip tone={priorityTone[key]} dot>{key}</Chip>}
            >
              <ul className="space-y-3">
                {list.map((it) => (
                  <li key={it.id} className="flex items-start gap-3">
                    <label className="mt-0.5 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={it.active}
                        onChange={(e) => toggle(it.id, e.target.checked)}
                        disabled={!!busy[it.id]}
                        className="sr-only peer"
                      />
                      <span
                        aria-hidden
                        className={`grid place-items-center size-5 rounded-[7px] border transition-all peer-disabled:opacity-50 ${
                          it.active
                            ? 'bg-accent border-accent text-accent-fg'
                            : 'bg-bg-sunken border-line-strong text-transparent peer-hover:border-fg-muted peer-focus-visible:border-accent'
                        }`}
                      >
                        <svg
                          viewBox="0 0 16 16"
                          className="size-3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 8.5l3.5 3.5L13 5" />
                        </svg>
                      </span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2 items-baseline">
                        <span className="font-medium text-fg">{it.label}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint shrink-0">
                          {it.cost_per_month}
                        </span>
                      </div>
                      <p className="text-xs text-fg-muted leading-relaxed mt-1">{it.detail}</p>
                      {/* An active item's edge is banked — green. An inactive
                          one is still only a projection — violet, the system's
                          "planned, not yet actual" mark. */}
                      {it.probability_hint && (
                        <p
                          className={`font-mono text-[10px] uppercase tracking-[0.14em] mt-1 ${
                            it.active ? 'text-good' : 'text-pop'
                          }`}
                        >
                          {it.probability_hint}
                        </p>
                      )}
                      {it.active && it.activated_at && (
                        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint mt-1">
                          activated {new Date(it.activated_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}

        {error && <pre className="text-xs text-danger">{error}</pre>}
      </div>
      <Nav />
    </>
  );
}
